import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FileText, ChevronRight, CheckCircle2, Clock, Download } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { BACKEND_URL } from "../../lib/api";

const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending: { l: "Pending", cls: "bg-amber-100 text-amber-700" },
  viewed: { l: "Viewed", cls: "bg-blue-100 text-blue-700" },
  completed: { l: "Signed", cls: "bg-emerald-100 text-emerald-700" },
  voided: { l: "Voided", cls: "bg-black/10 text-black/60" },
  declined: { l: "Declined", cls: "bg-red-100 text-red-600" },
  expired: { l: "Expired", cls: "bg-black/10 text-black/60" },
};

export default function MyAgreementsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    axios.get(`${API}/agreements/me`, { withCredentials: true })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, []);

  return (
    <div data-testid="my-agreements-page" className="space-y-6">
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Legal</p>
        <h1 className="font-serif font-light text-4xl">My Agreements</h1>
        <p className="text-[var(--muted)] text-sm mt-2">
          Documents assigned to {user?.email} — pending and signed.
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-black/10 rounded-2xl bg-white/40">
          <FileText className="w-10 h-10 text-black/20 mx-auto mb-4" />
          <p className="font-serif text-2xl">No agreements yet</p>
          <p className="text-sm text-black/50 mt-2">Agreements will appear here when sent to your email or auto-issued (e.g., contest entry).</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((a) => {
            const badge = STATUS_BADGE[a.status] || STATUS_BADGE.pending;
            return (
              <div key={a.agreement_id}
                className="bg-white border border-[var(--line)] rounded-xl p-5 flex items-center gap-4 flex-wrap"
                data-testid={`my-agreement-row-${a.agreement_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-serif text-xl">{a.title}</h3>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${badge.cls}`}>{badge.l}</span>
                  </div>
                  <p className="text-[11px] text-black/40 mt-1 font-mono">v{a.version} · {a.agreement_id}</p>
                  {a.signed_at && (
                    <p className="text-xs text-black/60 mt-1">
                      Signed {new Date(a.signed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                {a.status === "completed" ? (
                  <a href={`${API}/agreements/${a.agreement_id}/pdf`} target="_blank" rel="noopener"
                    className="btn-outline-gold text-sm" data-testid={`my-agreement-pdf-${a.agreement_id}`}>
                    <Download className="w-3 h-3" /> Download PDF
                  </a>
                ) : a.sign_token ? (
                  <Link to={`/agreements/sign/${a.sign_token}`} className="btn-gold text-sm"
                    data-testid={`my-agreement-sign-${a.agreement_id}`}>
                    <ChevronRight className="w-3 h-3" /> Open & Sign
                  </Link>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
